import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Plus, ImageIcon, Film, Music } from 'lucide-react';
import { ReactComponent as SettingsIcon } from '../../../assets/settings.svg';
import AiSettingsPanel from './AiSettingsPanel';
import MediaSelectorModal from '../PropertiesPanel/MediaSelectorModal';
import { aiAPI } from '../../../services/api';
import { getModelById, getDefaultVoiceId, acceptsImageUpload, getNextImageRole, getImageRoleLabel, getImageSlots } from '../../../config/aiModels';
import { estimateCreditCost } from '../../../config/creditPricing';
import { useAiEffectiveModel } from '../../../hooks/useAiEffectiveModel';
import { useCanvas } from '../../../hooks/useCanvas';
import { useAuth } from '../../../contexts/AuthContext';

const MEDIA_TYPE_OPTIONS = [
  { type: 'image', label: 'Image', icon: ImageIcon },
  // { type: 'video', label: 'Video', icon: Film },
  // { type: 'audio', label: 'Audio', icon: Music },
];

const inferAspectRatio = (w, h) => {
  if (!w || !h) return '16:9';
  const ratio = w / h;
  if (ratio > 1.2) return '16:9';
  if (ratio < 0.8) return '9:16';
  return '1:1';
};

const AiDialog = ({ isOpen, onClose, anchorRef, onGenerate }) => {
  const { user, updateCredits } = useAuth();
  const { canvasSize } = useCanvas();
  const dialogRef = useRef(null);
  const settingsPanelRef = useRef(null);
  const settingsBtnRef = useRef(null);
  const plusMenuRef = useRef(null);
  const mediaSelectorRef = useRef(null);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [plusMenuOpen, setPlusMenuOpen] = useState(false);
  const [mediaSelectorOpen, setMediaSelectorOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [prompt, setPrompt] = useState('');

  // Reference media selected via the + button (supports multiple)
  const [referenceMediaList, setReferenceMediaList] = useState([]);

  // Settings state (lifted so Generate can include it in the payload)
  const [selectedModel, setSelectedModel] = useState('kling-silent');
  const [aspectRatio, setAspectRatio] = useState(() => inferAspectRatio(canvasSize?.width, canvasSize?.height));
  const [outputCount, setOutputCount] = useState(1);
  const [length, setLength] = useState('5s');

  // Voiceover-specific settings
  const [selectedVoiceId, setSelectedVoiceId] = useState(getDefaultVoiceId());

  // Track whether the user has manually changed the aspect ratio
  const [userChangedAspect, setUserChangedAspect] = useState(false);

  // Inferred model + hint (centralized hook)
  const { effectiveModel, modelHint, userChangedModel, handleModelChange } = useAiEffectiveModel(
    selectedModel, prompt, aspectRatio, setSelectedModel
  );

  // Sync aspect ratio when canvas size changes (unless user manually picked one)
  useEffect(() => {
    if (userChangedAspect) return;
    const inferred = inferAspectRatio(canvasSize?.width, canvasSize?.height);
    setAspectRatio(inferred);
  }, [canvasSize?.width, canvasSize?.height, userChangedAspect]);

  // Close on outside click — only if the click is outside ALL our UI elements
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      // Don't close AiDialog while the media selector modal is open
      if (mediaSelectorOpen) return;

      const insideDialog = dialogRef.current?.contains(e.target);
      const insideSettings = settingsPanelRef.current?.contains(e.target);
      const insideAnchor = anchorRef?.current?.contains(e.target);
      const insidePlusMenu = plusMenuRef.current?.contains(e.target);

      if (!insideDialog && !insideSettings && !insideAnchor && !insidePlusMenu) {
        setSettingsOpen(false);
        setPlusMenuOpen(false);
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen, onClose, anchorRef, mediaSelectorOpen]);

  // Close on Escape — settings/plus menu first, then the whole dialog
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        if (plusMenuOpen) {
          setPlusMenuOpen(false);
        } else if (settingsOpen) {
          setSettingsOpen(false);
        } else {
          onClose();
        }
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose, settingsOpen, plusMenuOpen]);

  // Reset sub-panels when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setSettingsOpen(false);
      setPlusMenuOpen(false);
    }
  }, [isOpen]);

  // Re-assign image roles when the effective model changes (e.g. video→image)
  // so that a "startFrame" becomes a "reference" or vice-versa.
  useEffect(() => {
    setReferenceMediaList((prev) => {
      const slots = getImageSlots(effectiveModel);
      if (slots.length === 0) {
        // Model has no image slots — strip roles but keep the media attached
        const changed = prev.some((m) => m.type === 'image' && m.role);
        if (!changed) return prev;
        return prev.map((m) => (m.type === 'image' ? { ...m, role: null } : m));
      }

      // Rebuild roles: walk through images in order, assigning the next available slot
      const filled = {};
      const updated = prev.map((m) => {
        if (m.type !== 'image') return m;
        for (const slot of slots) {
          const max = slot.max || 1;
          const count = filled[slot.role] || 0;
          if (count < max) {
            filled[slot.role] = count + 1;
            if (m.role === slot.role) return m; // already correct
            return { ...m, role: slot.role };
          }
        }
        // More images than slots — clear role
        if (!m.role) return m;
        return { ...m, role: null };
      });

      // Only update state if something actually changed
      const same = updated.every((m, i) => m === prev[i]);
      return same ? prev : updated;
    });
  }, [effectiveModel]);

  // Handle media type selection from the + menu.
  // For images, auto-assign the correct role based on the model's imageSlots config.
  const handleMediaTypeSelect = useCallback((mediaType) => {
    setPlusMenuOpen(false);
    if (mediaSelectorRef.current) {
      setMediaSelectorOpen(true);
      mediaSelectorRef.current.open(mediaType, null, (result, name) => {
        const url = typeof result === 'string' ? result : result?.url;
        const thumbnailUrl = typeof result === 'object' ? result?.thumbnailUrl : null;
        if (url) {
          setReferenceMediaList((prev) => {
            // For images, auto-assign the role from the model's imageSlots
            let role = null;
            if (mediaType === 'image') {
              role = getNextImageRole(effectiveModel, prev);
            }
            return [
              ...prev,
              { type: mediaType, url, name: name || '', thumbnailUrl, ...(role && { role }) },
            ];
          });
        }
        setMediaSelectorOpen(false);
      });
    }
  }, [effectiveModel]);

  // Remove attached reference media by index
  const handleRemoveReference = useCallback((index) => {
    setReferenceMediaList((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Generate handler — builds JSON payload from prompt + settings + reference
  const handleGenerate = useCallback(async () => {
    if (!prompt.trim() || isGenerating) return;

    setIsGenerating(true);

    // Pre-generate a stable spriteId so both the canvas element and the
    // backend job share the same identifier.
    const spriteId = `ai_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Look up the model config to determine which settings to include
    const modelConfig = getModelById(effectiveModel);

    // Build settings — only include what the model supports
    const settings = { model: effectiveModel };

    if (modelConfig?.settings.aspectRatio) {
      settings.aspectRatio = aspectRatio;
    }
    if (modelConfig?.settings.outputCount) {
      settings.outputCount = outputCount;
    }
    if (modelConfig?.settings.length) {
      settings.length = length;
    }
    if (modelConfig?.settings.voices) {
      settings.voiceId = selectedVoiceId;
    }

    const payload = {
      prompt: prompt.trim(),
      settings,
      ...(referenceMediaList.length > 0 && { referenceMedia: referenceMediaList }),
    };

    // Await the API call — only create the sprite if the server accepts it
    try {
      const res = await aiAPI.generate({ spriteId, ...payload });
      const creditsRemaining = res.data?.data?.creditsRemaining;
      if (typeof creditsRemaining === 'number') {
        updateCredits(creditsRemaining);
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === 'INSUFFICIENT_CREDITS') {
        // Redirect to pricing page
        window.location.href = '/pricing';
      } else {
        console.error('AI generate request failed:', err);
      }
      setIsGenerating(false);
      return; // Don't create sprite or close dialog on failure
    }

    const aiComponentData = {
      type: 'ai',
      spriteId, // Used by createAiSprite as the elementId
      name: `AI: ${prompt.trim().slice(0, 30)}`,
      title: `AI: ${prompt.trim().slice(0, 30)}`,
      aiPayload: payload,
      generatedMedia: null, // Will be populated when generation completes
    };

    if (onGenerate) {
      onGenerate(aiComponentData);
    }

    // Close everything after generating
    setIsGenerating(false);
    setSettingsOpen(false);
    setPlusMenuOpen(false);
    onClose();
  }, [prompt, effectiveModel, aspectRatio, outputCount, length, selectedVoiceId, referenceMediaList, onClose, onGenerate, updateCredits, isGenerating]);

  if (!isOpen) return null;

  return (
    <>
      {/* Settings panel — sits above the dialog bar */}
      <AiSettingsPanel
        ref={settingsPanelRef}
        isOpen={settingsOpen}
        selectedModel={selectedModel}
        onModelChange={handleModelChange}
        aspectRatio={aspectRatio}
        onAspectRatioChange={(v) => { setAspectRatio(v); setUserChangedAspect(true); }}
        outputCount={outputCount}
        onOutputCountChange={setOutputCount}
        length={length}
        onLengthChange={setLength}
        selectedVoiceId={selectedVoiceId}
        onVoiceIdChange={setSelectedVoiceId}
        compact={true}
      />

      {/* Main dialog bar */}
      <div
        ref={dialogRef}
        className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 z-40 w-[540px] bg-ag-black-700 border border-ag-black-400 rounded-xl shadow-2xl"
      >
        {/* Reference media chips — shown above the input bar when media is attached */}
        {referenceMediaList.length > 0 && (
          <div className="flex items-center gap-2 px-3 pt-2 pb-1 flex-wrap">
            {referenceMediaList.map((media, index) => (
              <div
                key={index}
                className="relative group flex flex-col items-center bg-ag-black-600 rounded-lg overflow-hidden"
                style={{ width: 64 }}
              >
                {/* Thumbnail preview */}
                {media.type === 'image' ? (
                  <img
                    crossOrigin="anonymous"
                    src={media.thumbnailUrl || media.url}
                    alt={media.name || 'Reference'}
                    className="w-16 h-16 object-cover"
                  />
                ) : media.type === 'video' ? (
                  media.thumbnailUrl ? (
                    <img
                      crossOrigin="anonymous"
                      src={media.thumbnailUrl}
                      alt={media.name || 'Reference'}
                      className="w-16 h-16 object-cover"
                    />
                  ) : (
                    <div className="w-16 h-16 flex items-center justify-center bg-ag-black-500">
                      <Film size={20} className="text-ag-black-300" />
                    </div>
                  )
                ) : (
                  <div className="w-16 h-16 flex items-center justify-center bg-ag-black-500">
                    <Music size={20} className="text-ag-black-300" />
                  </div>
                )}
                {/* Role label overlay */}
                {media.role && (
                  <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-1 py-0.5 text-center">
                    <span className="text-[9px] font-sourceSans text-ag-green font-semibold leading-none">
                      {getImageRoleLabel(effectiveModel, media.role)}
                    </span>
                  </div>
                )}
                {/* Remove button */}
                <button
                  onClick={() => handleRemoveReference(index)}
                  className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/60 text-ag-black-300 hover:text-white hover:bg-black/80 transition-colors flex items-center justify-center text-[10px] leading-none opacity-0 group-hover:opacity-100"
                >
                  ×
                </button>
              </div>
            ))}
            {/* Next available image slot hint — e.g. "+ Last frame" for Kling after first frame is added */}
            {(() => {
              const nextRole = getNextImageRole(effectiveModel, referenceMediaList);
              if (!nextRole) return null;
              const label = getImageRoleLabel(effectiveModel, nextRole);
              return (
                <button
                  onClick={() => handleMediaTypeSelect('image')}
                  className="flex items-center gap-1.5 bg-ag-black-600 hover:bg-ag-black-500 rounded-lg px-2.5 py-1 text-xs font-sourceSans text-ag-black-300 hover:text-white transition-colors border border-dashed border-ag-black-400"
                >
                  <ImageIcon size={12} />
                  <span>+ {label}</span>
                </button>
              );
            })()}
          </div>
        )}

        {/* Image slot hints — shown when no media is attached yet but model accepts images */}
        {referenceMediaList.length === 0 && getImageSlots(effectiveModel).length > 0 && (
          <div className="flex items-center gap-2 px-3 pt-2 pb-1">
            {getImageSlots(effectiveModel).map((slot) => (
              <button
                key={slot.role}
                onClick={() => handleMediaTypeSelect('image')}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-ag-black-600 hover:bg-ag-black-500 text-xs font-sourceSans text-ag-black-300 hover:text-white transition-colors"
              >
                <ImageIcon size={12} />
                + {slot.label}
              </button>
            ))}
          </div>
        )}

        {/* Input row */}
        <div className="px-3 pt-2 pb-1">
          <input
            type="text"
            value={prompt}
            onFocus={() => {
              setSettingsOpen(false);
              setPlusMenuOpen(false);
            }}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !isGenerating) handleGenerate(); }}
            placeholder="Describe what you want to do"
            className="w-full bg-transparent text-sm font-sourceSans text-white placeholder-gray-500 outline-none italic"
            autoFocus
          />
        </div>

        {/* Controls row: +, settings, hint, generate */}
        <div className="flex items-center gap-2.5 px-2 py-1">
          {/* Plus button with popover */}
          <div className="relative">
            <button
              onClick={() => {
                setPlusMenuOpen((prev) => !prev);
                setSettingsOpen(false);
              }}
              className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
                plusMenuOpen ? 'bg-ag-black-500' : 'bg-ag-black-800 hover:bg-ag-black-500'
              }`}
            >
              <Plus size={16} className="text-white" />
            </button>

            {/* Plus menu popover */}
            {plusMenuOpen && (
              <div
                ref={plusMenuRef}
                className="absolute bottom-full mb-2 left-0 z-[60] bg-ag-black-700 border border-ag-black-600 rounded-lg shadow-2xl overflow-hidden min-w-[140px]"
              >
                {MEDIA_TYPE_OPTIONS.map(({ type, label, icon: Icon }) => (
                  <button
                    key={type}
                    onClick={() => handleMediaTypeSelect(type)}
                    className="flex items-center gap-2.5 w-full px-3 py-2.5 text-sm font-sourceSans text-ag-black-300 hover:bg-ag-black-600 hover:text-white transition-colors"
                  >
                    <Icon size={14} className="opacity-60" />
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Settings icon */}
          <button
            ref={settingsBtnRef}
            onClick={() => {
              setSettingsOpen((prev) => !prev);
              setPlusMenuOpen(false);
            }}
            className="flex-shrink-0 p-1 transition-opacity hover:opacity-80"
          >
            <SettingsIcon
              className={`w-[13px] h-[11px] transition-colors ${
                settingsOpen
                  ? '[&_circle]:stroke-white [&_path]:stroke-white'
                  : '[&_circle]:stroke-[#4A4A4A] [&_path]:stroke-[#4A4A4A]'
              }`}
            />
          </button>

          {/* Model hint — always visible, shows what will be generated */}
          {modelHint && (
            <span className="flex-1 text-[11px] font-sourceSans text-ag-black-400 truncate">
              {modelHint}{!userChangedModel && ' Need another format? Use settings.'}
            </span>
          )}

          {/* Credit cost + Generate button */}
          {(() => {
            const cost = estimateCreditCost(effectiveModel, { prompt, length, outputCount });
            const credits = user?.credits ?? 0;
            const canAfford = credits >= (cost || 0);
            return (
              <>
                {cost != null && (
                  <span className={`text-[11px] font-sourceSans whitespace-nowrap ${canAfford ? 'text-ag-green/70' : 'text-red-400'}`}>
                    ⚡ {cost} {cost === 1 ? 'credit' : 'credits'}
                  </span>
                )}
                {canAfford ? (
                  <button
                    onClick={handleGenerate}
                    disabled={isGenerating}
                    className={`flex-shrink-0 px-4 py-1 text-sm font-sourceSans font-semibold rounded-lg transition-colors whitespace-nowrap ${
                      isGenerating
                        ? 'bg-ag-green/50 text-black/50 cursor-not-allowed'
                        : 'bg-ag-green text-black hover:bg-ag-green/90'
                    }`}
                  >
                    {isGenerating ? 'Generating…' : 'Generate'}
                  </button>
                ) : (
                  <a
                    href="/manage-subscription"
                    className="flex-shrink-0 px-4 py-1 bg-red-600 text-white text-sm font-sourceSans font-semibold rounded-lg hover:bg-red-500 transition-colors whitespace-nowrap text-center"
                  >
                    Upgrade
                  </a>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {/* Media Selector Modal — rendered via portal to escape overflow:hidden parents */}
      {ReactDOM.createPortal(
        <MediaSelectorModal ref={mediaSelectorRef} onClose={() => setMediaSelectorOpen(false)} />,
        document.body
      )}
    </>
  );
};

export default AiDialog;
